import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Loader2, Activity, ArrowLeft } from "lucide-react";
import TenantSignalsPanel from "@/components/command_center/TenantSignalsPanel";
import TenantMetricsPanel from "@/components/command_center/TenantMetricsPanel";
import SourcesPanel from "@/components/command_center/SourcesPanel";
import WarningsPanel from "@/components/command_center/WarningsPanel";
import V2TenantSection from "@/components/command_center/V2TenantSection";

const STATUS_BADGE = {
  healthy:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  warning:  "bg-amber-100 text-amber-700 border-amber-200",
  degraded: "bg-orange-100 text-orange-700 border-orange-200",
  critical: "bg-red-100 text-red-700 border-red-200",
  unknown:  "bg-gray-100 text-gray-600 border-gray-200",
};

export default function CommandCenterTenantPage() {
  const { tenantId } = useParams();
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get(`/whatsapp/command-center/tenant/${tenantId}`);
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Erro ao carregar tenant";
      setError(msg);
      toast.error("Erro ao carregar drilldown do tenant");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authAxios, tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <Activity className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => fetchData()}>
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  const st  = data?.health_status || "unknown";
  const cls = STATUS_BADGE[st] || STATUS_BADGE.unknown;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/command-center">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Visão Geral
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Tenant Drilldown</h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {tenantId} · {data?.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" disabled={refreshing} onClick={() => fetchData(true)}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <div className="text-xs text-muted-foreground mb-2">Estado</div>
          <Badge className={`text-xs border ${cls}`}>{st}</Badge>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-xs text-muted-foreground mb-2">Score</div>
          <div className="text-2xl font-bold text-gray-800">{data?.health_score ?? "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-xs text-muted-foreground mb-2">Modo</div>
          <div className="text-sm font-medium text-gray-800">{data?.effective_mode ?? "—"}</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TenantSignalsPanel signals={data?.signals ?? {}} />
        <TenantMetricsPanel metrics={data?.metrics ?? {}} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SourcesPanel  sources={data?.source_status ?? {}} />
        <WarningsPanel warnings={data?.warnings ?? []} />
      </div>

      <V2TenantSection tenantId={tenantId} />
    </div>
  );
}
